import { ExportKind } from '../entity/api/export';
import {
  type CreateBulkGroupReq,
  type CreateBulkGroupRes,
  type DelBulkGroupRes,
  type ExportBulkGroupRes,
  type GetBulkGroupByBrandIdRes,
  type GetBulkGroupProductsRes,
  type ListRes,
  type UpdateBulkGroupReq,
  type UpdateBulkGroupRes,
  createBulkGroupReqSchema,
  delBulkGroupReqSchema,
  exportBulkGroupReqSchema,
  getBulkGroupByBrandIdReqSchema,
  getBulkGroupProductsReqSchema,
} from '../entity/api/routes/bulk-group';
import { type ProductSortAndFilter } from '../entity/api/routes/product';
import { dasherize } from '../shared/string';
import { removeElement } from '../shared/tiptap/dom-utilities';
import { apiRequest, setURL } from './request';

export async function createBulkGroup(data: CreateBulkGroupReq['body'], files: File | File[]) {
  const validatedBody = createBulkGroupReqSchema.shape.body.parse(data);

  const formData = new FormData();

  // Append all body data to formData
  for (const [key, value] of Object.entries(validatedBody)) {
    if (value !== null && value !== undefined) {
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    }
  }

  // Handle file(s)
  if (Array.isArray(files)) {
    for (const file of files) {
      formData.append('file', file);
    }
  } else {
    formData.append('file', files);
  }

  const url = setURL('/bulk-group', null);
  const res = await apiRequest<CreateBulkGroupRes>(url, 'POST', true, formData);
  return res.data;
}

export function getBulkGroupsList(): Promise<ListRes> {
  return apiRequest<ListRes>('/bulk-group/list', 'GET', true);
}

export async function getBulkGroupByBrandId(brandId: string) {
  const req = getBulkGroupByBrandIdReqSchema.parse({
    params: { id: brandId },
    body: null,
  });
  const url = setURL(`/bulk-group/get-by-brand/${brandId}`, req.query);
  return apiRequest<GetBulkGroupByBrandIdRes>(url, 'GET', true, req.body);
}

export function updateBulkGroup(
  id: string,
  data: UpdateBulkGroupReq['body']
): Promise<UpdateBulkGroupRes> {
  const url = setURL(`/bulk-group/${id}`, null);
  return apiRequest<UpdateBulkGroupRes>(url, 'PATCH', true, data);
}

export function removeBulkGroup(
  id: string,
  shouldRemoveProducts: boolean
): Promise<DelBulkGroupRes> {
  const url = setURL(`/bulk-group/${id}`, null);
  const req = delBulkGroupReqSchema.parse({
    params: { id },
    body: {
      shouldRemoveProducts,
    },
  });
  return apiRequest<DelBulkGroupRes>(url, 'DELETE', true, req.body);
}

export function getBulkGroupById(
  id: string,
  queryParams?: ProductSortAndFilter
): Promise<GetBulkGroupProductsRes> {
  const req = getBulkGroupProductsReqSchema.parse({
    params: { id },
    query: queryParams,
    body: null,
  });
  const url = setURL(`/bulk-group/${id}`, req.query);
  return apiRequest<GetBulkGroupProductsRes>(url, 'GET', true);
}

export async function exportBulkGroupToCsv(id: string, bulkGroupName: string) {
  const req = exportBulkGroupReqSchema.parse({
    params: { id },
    body: {
      kind: ExportKind.CSV,
    },
  });
  const url = setURL(`/bulk-group/${id}/export`, req.query);
  const result = await apiRequest<ExportBulkGroupRes>(url, 'POST', true, req.body);
  const fileName = dasherize(bulkGroupName);
  const blob = new Blob([result.data], { type: 'text/csv' });
  const clickUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = clickUrl;
  link.setAttribute('download', `${fileName ?? `product_${id}.csv`}`);
  document.body.appendChild(link);
  link.click();
  removeElement(document.body, link);
  return true;
}
